import { useState, useRef, useCallback, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl, thumbUrl } from "@/lib/api";
import { readGarmentPick, clearGarmentPick } from "@/pages/Merchandise";
import { X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumField } from "@/components/NumField";
import { SERVICES, lineUnitPrice } from "@/lib/service";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, RotateCw, Copy, Trash2, Wand2, Layers, Plus, Minus, Save, AlertTriangle, Eraser, CheckSquare, Square, Grid3x3, Eye, Crop } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { BuilderGuide, BuilderGuideButton, useBuilderGuide } from "@/components/BuilderGuide";
import { FieldHint, HINTS } from "@/components/FieldHint";
import { SaveImageToCatalog } from "@/components/SaveImageToCatalog";
import { CropDialog } from "@/components/CropDialog";
import { GuidelinesLink } from "@/components/GuidelinesLink";

const CANVAS_W = 460; // px for 22in width
const DPI = 300; // px per inch for px/inch conversion
const LOW_DPI = 150; // print-quality warning threshold

function dpiColor(dpi) {
  if (dpi >= 300) return "text-[#10b981] border-[#10b981]/40 bg-[#10b981]/10";
  if (dpi >= 200) return "text-[#ffd600] border-[#ffd600]/40 bg-[#ffd600]/10";
  return "text-red-400 border-red-500/40 bg-red-500/10";
}

export default function GangSheetBuilder() {
  const { addItem, setDrawerOpen, items: cartItems } = useCart();
  const { data: presets = [] } = useQuery({ queryKey: ["presets"], queryFn: async () => (await api.get("/sheet-presets")).data });
  const { data: designs = [] } = useQuery({ queryKey: ["designs"], queryFn: async () => (await api.get("/designs")).data });
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tplName, setTplName] = useState("");
  const { data: templates = [] } = useQuery({ queryKey: ["templates"], queryFn: async () => (await api.get("/sheet-templates")).data, enabled: !!user });

  const saveTemplate = async () => {
    if (!tplName.trim()) { toast.error("Name your template"); return; }
    if (items.length === 0) { toast.error("Add designs to the sheet first"); return; }
    if (guardDims()) return;
    try {
      await api.post("/sheet-templates", { name: tplName.trim(), preset_name: preset.name, sheet_height: sheetH, items: items.map((i) => ({ ...i, hIn: hOf(i) })) });
      toast.success("Template saved");
      setTplName("");
      qc.invalidateQueries({ queryKey: ["templates"] });
    } catch { toast.error("Could not save template"); }
  };
  const loadTemplate = (t) => {
    const idx = presets.findIndex((p) => p.name === t.preset_name);
    if (idx >= 0) setPresetIdx(idx);
    if (t.sheet_height) setCustomHeight(t.sheet_height);
    setItems((t.items || []).map((it) => ({ ...it, id: crypto.randomUUID() })));
    setSelectedId(null);
    toast.success(`Loaded ${t.name}`);
  };
  const deleteTemplate = async (id) => { await api.delete(`/sheet-templates/${id}`); qc.invalidateQueries({ queryKey: ["templates"] }); };
  const reorderTemplate = (t) => {
    const pi = presets.findIndex((p) => p.name === t.preset_name);
    const pset = pi >= 0 ? presets[pi] : preset;
    if (!pset) { toast.error("Could not resolve sheet size"); return; }
    const sh = pset.height_in || t.sheet_height || customHeight;
    const uprice = pset.price != null ? pset.price : +(pset.rate_per_inch * (t.sheet_height || customHeight)).toFixed(2);
    const layout = (t.items || []).map((i) => ({ src: i.src, xIn: +(+i.xIn).toFixed(2), yIn: +(+i.yIn).toFixed(2), wIn: +(+i.wIn).toFixed(2), rot: i.rot || 0, hIn: i.hIn != null ? +(+i.hIn).toFixed(2) : null }));
    if (layout.length === 0) { toast.error("This template is empty"); return; }
    loadTemplate(t);
    addItem({
      kind: "gangsheet", name: `Gang Sheet — ${pset.name}`, quantity: 1, unit_price: uprice,
      sheet_name: pset.name, sheet_width: pset.width_in, sheet_height: sh,
      image_url: layout[0].src, preview_url: layout[0].src, layout,
    });
    toast.success(`Reordered ${t.name} — added to cart`);
  };

  const [presetIdx, setPresetIdx] = useState(1);
  const [customHeight, setCustomHeight] = useState(48);
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [delSel, setDelSel] = useState(() => new Set());
  const [qty, setQty] = useState(1);
  const [service, setService] = useState(() => (readGarmentPick() ? "press_merch" : "print_only"));
  const [pressQty, setPressQty] = useState(null);
  const { data: pressCfg } = useQuery({ queryKey: ["press-fee"], queryFn: async () => (await api.get("/press-fee")).data, staleTime: 300000 });
  const pressFee = pressCfg?.fee ?? 4;
  const [building, setBuilding] = useState(false);
  const [garment, setGarment] = useState(() => readGarmentPick());
  const garmentLabel = garment ? `${garment.name}${garment.color ? ` · ${garment.color}` : ""}${garment.size ? ` · ${garment.size}` : ""}` : null;
  const garmentFields = garment ? { garment: { product_id: garment.product_id, name: garment.name, color: garment.color, size: garment.size } } : {};
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snapGrid, setSnapGrid] = useState(true);
  const [printPreview, setPrintPreview] = useState(null);
  const [trimSaved, setTrimSaved] = useState(0);
  const [saveImgSrc, setSaveImgSrc] = useState(null);
  const [cropTarget, setCropTarget] = useState(null);
  const [notifyCustomer, setNotifyCustomer] = useState(false);
  const [nestedAt, setNestedAt] = useState(0);
  const [unit, setUnit] = useState("in");
  const [aspectLock, setAspectLock] = useState(true);
  const [editCtx, setEditCtx] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [addCtx, setAddCtx] = useState(null);
  const [approveNow, setApproveNow] = useState(false);
  const [libCtx, setLibCtx] = useState(null);
  const [libOpen, setLibOpen] = useState(false);
  const [libName, setLibName] = useState("");
  const [libFolder, setLibFolder] = useState("");
  const isAdmin = user?.role === "admin";
  const libraryMode = searchParams.get("library") === "1";
  const libraryFolder = searchParams.get("folder") || "";
  const librarySheetId = searchParams.get("sheetId");
  const { data: sheetFolders = [] } = useQuery({ queryKey: ["sheet-folders"], queryFn: async () => (await api.get("/admin/sheet-folders")).data, enabled: isAdmin });
  const canvasRef = useRef(null);
  const drag = useRef(null);
  const resize = useRef(null);
  const rotate = useRef(null);

  const preset = presets[presetIdx];
  const sheetW = preset ? preset.width_in : 12;
  const sheetH = preset ? (preset.height_in || customHeight) : 48;
  const scale = CANVAS_W / sheetW;
  const canvasH = sheetH * scale;
  const price = preset ? (preset.price != null ? preset.price : +(preset.rate_per_inch * customHeight).toFixed(2)) : 0;
  const effPressQty = service === "press_own" ? (pressQty ?? Math.max(1, items.length)) : 0;
  const linePrice = lineUnitPrice(price, service, effPressQty, pressFee);
  const serviceFields = { service, press_qty: service === "press_own" ? effPressQty : null, press_fee: service === "press_own" ? pressFee : null, sheet_price: price };

  const addImage = (src, natW, natH) => {
    const id = crypto.randomUUID();
    const wIn = Math.max(1, Math.min(sheetW - 1, +((natW || 300) / 300).toFixed(2)));
    setItems((p) => {
      const off = (p.length % 6) * 1.2;
      return [...p, { id, src, natW, natH, xIn: 0.5 + off, yIn: 0.5 + off, wIn, rot: 0 }];
    });
    setSelectedId(id);
  };

  const [upProg, setUpProg] = useState(null);
  const shrinkForUpload = (f) => new Promise((resolve) => {
    if (!/^image\/(png|jpeg)$/.test(f.type) || f.size < 3 * 1024 * 1024) return resolve(f);
    let done = false; const finish = (v) => { if (!done) { done = true; resolve(v); } };
    setTimeout(() => finish(f), 6000);
    const url = URL.createObjectURL(f); const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const MAX = 4000, m = Math.max(img.naturalWidth, img.naturalHeight);
      if (m <= MAX) return finish(f);
      const s = MAX / m, c = document.createElement("canvas");
      c.width = Math.round(img.naturalWidth * s); c.height = Math.round(img.naturalHeight * s);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      c.toBlob((b) => finish(b ? new File([b], f.name, { type: f.type }) : f), f.type, 0.92);
    };
    img.onerror = () => { URL.revokeObjectURL(url); finish(f); };
    img.src = url;
  });
  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const prog = {}; const tick = () => { const v = Object.values(prog); setUpProg({ pct: Math.round(v.reduce((a, b) => a + b, 0) / v.length), n: files.length }); };
    await Promise.all(files.map(async (f, idx) => {
      prog[idx] = 0; tick();
      try {
        const file = await shrinkForUpload(f);
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/upload", fd, { onUploadProgress: (ev) => { if (ev.total) { prog[idx] = Math.round((ev.loaded / ev.total) * 90); tick(); } } });
        prog[idx] = 100; tick();
        const url = fileUrl(data.url);
        const dims = data.optimized?.dims;
        if (data.content_type === "application/pdf") addImage(url, 300, 300);
        else if (dims?.length === 2) addImage(url, dims[0], dims[1]);
        else {
          const img = new Image(); let done = false;
          const finish = (w, h) => { if (!done) { done = true; addImage(url, w, h); } };
          img.onload = () => finish(img.naturalWidth, img.naturalHeight);
          img.onerror = () => finish(1200, 1200);
          setTimeout(() => finish(1200, 1200), 8000);
          img.src = url;
        }
        toast.success(`Uploaded ${f.name}${data.duplicate ? " (already on file — reused)" : ""}`);
      } catch (err) { toast.error(`${f.name}: upload failed (PNG/JPEG/PDF only)`); }
    }));
    setTimeout(() => setUpProg(null), 600);
  };

  const addDesign = (d) => {
    const img = new Image();
    img.onload = () => addImage(fileUrl(d.image_url), img.naturalWidth, img.naturalHeight);
    img.onerror = () => addImage(fileUrl(d.image_url), 1024, 1024);
    img.src = fileUrl(d.image_url);
  };

  const updateItem = (id, patch) => setItems((p) => p.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  const removeItem = (id) => { setItems((p) => p.filter((i) => i.id !== id)); if (selectedId === id) setSelectedId(null); };
  const duplicate = (id) => { const it = items.find((i) => i.id === id); if (it) setItems((p) => [...p, { ...it, id: crypto.randomUUID(), xIn: it.xIn + 0.5, yIn: it.yIn + 0.5 }]); };
  const duplicateN = (id, n) => { const it = items.find((i) => i.id === id); if (!it || n < 1) return; const clones = []; for (let k = 1; k <= n; k++) clones.push({ ...it, id: crypto.randomUUID(), xIn: it.xIn + 0.3 * k, yIn: it.yIn + 0.3 * k }); setItems((p) => [...p, ...clones]); };
  const fillSheet = (it) => {
    if (!it) return;
    const w = it.wIn, h = hOf(it), gap = 0.15, m = 0.5;
    const cols = Math.max(1, Math.floor((sheetW - m + gap) / (w + gap)));
    const rows = Math.max(1, Math.floor((sheetH - m + gap) / (h + gap)));
    const total = cols * rows;
    if (!window.confirm(`Fill the sheet with ${total} copies of this image? This replaces the current layout.`)) return;
    const next = [];
    for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
      next.push({ ...it, id: crypto.randomUUID(), xIn: +(m / 2 + c * (w + gap)).toFixed(2), yIn: +(m / 2 + r * (h + gap)).toFixed(2), wIn: w, hIn: it.hIn != null ? h : null, rot: 0 });
    }
    setItems(next); setSelectedId(next[0].id);
    toast.success(`Filled with ${total} copies`);
  };
  const [copies, setCopies] = useState(2);
  const [guideOpen, setGuideOpen] = useBuilderGuide();
  const [notes, setNotes] = useState("");
  const toggleDel = (id) => setDelSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setDelSel((p) => (p.size === items.length && items.length > 0 ? new Set() : new Set(items.map((i) => i.id))));
  const toDelete = () => { const s = new Set(delSel); if (selectedId) s.add(selectedId); return s; };
  const deleteSelected = () => {
    const s = toDelete();
    if (s.size === 0) { toast.error("Click an image (or tick several) first"); return; }
    const n = s.size;
    setItems((p) => p.filter((i) => !s.has(i.id)));
    setSelectedId(null);
    setDelSel(new Set());
    toast.success(`Deleted ${n} item${n === 1 ? "" : "s"}`);
  };
  useEffect(() => {
    const onKey = (e) => {
      if ((e.key !== "Delete" && e.key !== "Backspace") || ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName) || e.target.isContentEditable) return;
      if (toDelete().size) { e.preventDefault(); deleteSelected(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const [bgRemovingId, setBgRemovingId] = useState(null);
  const fitTo300 = (it) => {
    const w = Math.max(0.5, Math.min(sheetW - 0.25, +(it.natW / DPI).toFixed(2)));
    updateItem(it.id, { wIn: w, hIn: null });
  };
  const fixAllTo300 = () => {
    let n = 0;
    setItems((p) => p.map((it) => {
      if (effDpi(it) >= LOW_DPI) return it;
      n += 1;
      return { ...it, wIn: Math.max(0.5, Math.min(sheetW - 0.25, +(it.natW / DPI).toFixed(2))), hIn: null };
    }));
    toast.success(`Resized ${n} design${n === 1 ? "" : "s"} to 300 DPI`);
  };
  const removeBg = async (it) => {
    setBgRemovingId(it.id);
    const tid = toast.loading("Removing background…");
    try {
      const { data } = await api.post("/remove-background", { url: it.src });
      const newUrl = fileUrl(data.url);
      const img = new Image();
      img.onload = () => updateItem(it.id, { src: newUrl, natW: img.naturalWidth, natH: img.naturalHeight });
      img.onerror = () => updateItem(it.id, { src: newUrl });
      img.src = newUrl;
      toast.success("Background removed", { id: tid });
    } catch (err) {
      toast.error("Could not remove background", { id: tid });
    } finally { setBgRemovingId(null); }
  };

  const onPointerDown = (e, it) => {
    e.stopPropagation();
    if (e.shiftKey || e.ctrlKey || e.metaKey) { toggleDel(it.id); return; }
    setSelectedId(it.id);
    const rect = canvasRef.current.getBoundingClientRect();
    drag.current = { id: it.id, startX: e.clientX, startY: e.clientY, origX: it.xIn, origY: it.yIn, rect };
  };
  const onResizeDown = (e, it) => {
    e.stopPropagation();
    setSelectedId(it.id);
    resize.current = { id: it.id, startX: e.clientX, startY: e.clientY, origW: it.wIn, origH: (it.hIn != null ? it.hIn : it.wIn * (it.natH / it.natW || 1)) };
  };
  const onRotateDown = (e, it) => {
    e.stopPropagation();
    setSelectedId(it.id);
    const rect = canvasRef.current.getBoundingClientRect();
    const cx = rect.left + (it.xIn + it.wIn / 2) * scale;
    const cy = rect.top + (it.yIn + hOf(it) / 2) * scale;
    rotate.current = { id: it.id, cx, cy };
  };
  const onPointerMove = useCallback((e) => {
    if (rotate.current) {
      const { id, cx, cy } = rotate.current;
      let deg = Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI + 90;
      deg = (deg + 360) % 360;
      const snap = [0, 90, 180, 270, 360];
      for (const s of snap) if (Math.abs(deg - s) <= 4) { deg = s % 360; break; }
      const rot = Math.round(deg);
      setItems((p) => p.map((i) => (i.id === id ? { ...i, rot } : i)));
      return;
    }
    if (resize.current) {
      const { id, startX, startY, origW, origH } = resize.current;
      const dxIn = (e.clientX - startX) / scale;
      const dyIn = (e.clientY - startY) / scale;
      setItems((p) => p.map((i) => {
        if (i.id !== id) return i;
        const wIn = Math.max(0.5, Math.min(sheetW - 0.25, origW + dxIn));
        if (aspectLock) return { ...i, wIn, hIn: null };
        return { ...i, wIn, hIn: Math.max(0.5, origH + dyIn) };
      }));
      return;
    }
    if (!drag.current) return;
    const { id, startX, startY, origX, origY } = drag.current;
    const dxIn = (e.clientX - startX) / scale;
    const dyIn = (e.clientY - startY) / scale;
    const snap = (v) => (snapGrid ? Math.round(v / 0.25) * 0.25 : v);
    setItems((p) => p.map((i) => i.id === id ? { ...i, xIn: Math.max(0, snap(origX + dxIn)), yIn: Math.max(0, snap(origY + dyIn)) } : i));
  }, [scale, sheetW, aspectLock, snapGrid]);
  const onPointerUp = () => { drag.current = null; resize.current = null; rotate.current = null; };

  const hOf = (it) => (it.hIn != null ? it.hIn : it.wIn * (it.natH / it.natW || 1));
  const serializeLayout = () => items.map((i) => ({ src: i.src, xIn: +i.xIn.toFixed(2), yIn: +i.yIn.toFixed(2), wIn: +i.wIn.toFixed(2), hIn: +hOf(i).toFixed(2), rot: i.rot || 0 }));
  const dimsPending = items.some((i) => i.pending);
  const loadDims = (item) => {
    const im = new Image();
    im.onload = () => setItems((p) => p.map((x) => x.id === item.id ? { ...x, natW: im.naturalWidth, natH: im.naturalHeight, pending: false } : x));
    im.onerror = () => setItems((p) => p.map((x) => x.id === item.id ? { ...x, pending: false } : x));
    im.src = item.src;
  };
  const guardDims = () => { if (dimsPending) { toast.error("Still loading design sizes — try again in a moment"); return true; } return false; };
  const effDpi = (it) => Math.max(1, Math.round(Math.min(it.natW / it.wIn, it.natH / hOf(it))));

  const autoArrange = () => {
    if (guardDims()) return;
    const gap = 0.2, m = 0.2, usable = sheetW - 2 * m, eps = 1e-6;
    let x = m, y = m, rowH = 0;
    setItems((prev) => prev.map((it) => {
      const w = it.wIn, h = hOf(it);
      // Decide orientation: rotate 90° when it helps the piece fit the row / sheet width.
      let rot = 0, fw = w, fh = h;
      const fitsRow0 = x + w <= sheetW - m + eps;
      const fitsRow90 = x + h <= sheetW - m + eps;
      if ((!fitsRow0 && fitsRow90) || (w > usable && h <= usable)) { rot = 90; fw = h; fh = w; }
      // Wrap to a new shelf if the chosen footprint doesn't fit the current row.
      if (x + fw > sheetW - m + eps) {
        x = m; y += rowH + gap; rowH = 0;
        if (w > usable && h <= usable) { rot = 90; fw = h; fh = w; } else { rot = 0; fw = w; fh = h; }
      }
      // Footprint top-left is (x, y); convert to the (center-rotated) unrotated box origin.
      const cx = x + fw / 2, cy = y + fh / 2;
      const xIn = +(cx - w / 2).toFixed(2);
      const yIn = +(cy - h / 2).toFixed(2);
      x += fw + gap; rowH = Math.max(rowH, fh);
      return { ...it, xIn, yIn, rot };
    }));
    setNestedAt(Date.now());
  };

  const usedArea = items.reduce((s, it) => s + it.wIn * hOf(it), 0);
  const fill = Math.min(100, (usedArea / (sheetW * sheetH)) * 100);

  const selected = items.find((i) => i.id === selectedId);
  const selDpi = selected ? effDpi(selected) : 0;
  const lowDpiItems = items.filter((it) => effDpi(it) < LOW_DPI);
  const bboxOf = (it) => {
    const w = it.wIn, h = hOf(it), r = ((it.rot || 0) * Math.PI) / 180;
    const bw = Math.abs(w * Math.cos(r)) + Math.abs(h * Math.sin(r)), bh = Math.abs(w * Math.sin(r)) + Math.abs(h * Math.cos(r));
    const cx = it.xIn + w / 2, cy = it.yIn + h / 2;
    return { x1: cx - bw / 2, y1: cy - bh / 2, x2: cx + bw / 2, y2: cy + bh / 2 };
  };
  const contentBottom = items.length ? Math.max(...items.map((it) => bboxOf(it).y2)) : 0;
  const neededLen = items.length ? Math.max(2, Math.ceil(contentBottom + 0.2)) : 0;
  const efficiency = (() => {
    if (!preset || items.length === 0) return null;
    const wastePct = Math.max(0, 100 - fill);
    if (preset.height_in == null) {
      const saving = neededLen < customHeight ? +(preset.rate_per_inch * (customHeight - neededLen)).toFixed(2) : 0;
      return { wastePct, neededLen, suggest: neededLen < customHeight ? { label: `${neededLen}" roll`, saving, apply: () => { setCustomHeight(neededLen); setTrimSaved((v) => +(v + saving).toFixed(2)); } } : null, over: neededLen > customHeight };
    }
    const alt = presets.map((p, i) => ({ p, i })).filter(({ p }) => p.width_in === preset.width_in && p.height_in != null && p.height_in >= neededLen && p.price != null && p.price < price).sort((a, b) => a.p.price - b.p.price)[0];
    return { wastePct, neededLen, suggest: alt ? { label: alt.p.name, saving: +(price - alt.p.price).toFixed(2), apply: () => { setPresetIdx(alt.i); setTrimSaved((v) => +(v + price - alt.p.price).toFixed(2)); } } : null, over: neededLen > sheetH };
  })();
  const overlapIds = (() => {
    const set = new Set(), eps = 0.01, bb = items.map((it) => [it.id, bboxOf(it)]);
    for (let a = 0; a < bb.length; a++) for (let b = a + 1; b < bb.length; b++) {
      const A = bb[a][1], B = bb[b][1];
      if (A.x1 < B.x2 - eps && B.x1 < A.x2 - eps && A.y1 < B.y2 - eps && B.y1 < A.y2 - eps) { set.add(bb[a][0]); set.add(bb[b][0]); }
    }
    return set;
  })();
  const fixOverlaps = () => {
    if (guardDims()) return;
    const step = 0.25, maxIter = 400;
    const next = items.map((it) => ({ ...it }));
    const inter = (A, B) => A.x1 < B.x2 - 0.01 && B.x1 < A.x2 - 0.01 && A.y1 < B.y2 - 0.01 && B.y1 < A.y2 - 0.01;
    let moved = 0;
    for (let iter = 0; iter < maxIter; iter++) {
      let any = false;
      for (let a = 0; a < next.length; a++) for (let b = a + 1; b < next.length; b++) {
        const A = bboxOf(next[a]), B = bboxOf(next[b]);
        if (!inter(A, B)) continue;
        any = true;
        const mover = next[b];
        const dxRight = A.x2 - B.x1 + 0.2, dyDown = A.y2 - B.y1 + 0.2;
        const bw = B.x2 - B.x1;
        if (B.x1 + dxRight + bw <= sheetW && dxRight <= dyDown) mover.xIn = +(Math.round((mover.xIn + dxRight) / step) * step).toFixed(2);
        else { mover.yIn = +(Math.round((mover.yIn + dyDown) / step) * step).toFixed(2); if (B.x1 + bw > sheetW) mover.xIn = Math.max(0, sheetW - bw); }
        moved++;
      }
      if (!any) break;
    }
    if (!moved) { toast.info("No overlaps to fix"); return; }
    setItems(next);
    toast.success("Overlapping designs nudged apart");
  };
  useEffect(() => {
    if (!nestedAt) return;
    if (efficiency?.suggest) toast.success(`Auto-nested · ${efficiency.suggest.label} would fit${efficiency.suggest.saving > 0 ? ` (save $${efficiency.suggest.saving.toFixed(2)})` : ""}`, { action: { label: "Trim to fit", onClick: efficiency.suggest.apply }, duration: 8000 });
    else toast.success("Auto-nested · rotates to fit · 0.2\" gaps & margins");
  }, [nestedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const openPrintPreview = async (onConfirm) => {
    if (items.length === 0) { toast.error("Add at least one design to the sheet"); return; }
    if (guardDims()) return;
    setBuilding(true);
    const url = await buildPreview().catch(() => null);
    setBuilding(false);
    if (!url) { toast.error("Could not render the print preview"); if (onConfirm) onConfirm(null); return; }
    setPrintPreview({ url, onConfirm });
  };

  const buildPreview = async () => {
    try {
      const { data } = await api.post("/gangsheet/preview", {
        layout: serializeLayout(),
        sheet_width: sheetW, sheet_height: sheetH,
      }, { timeout: 25000 });
      if (data?.url) return fileUrl(data.url);
    } catch (e) { console.warn("server preview failed, falling back to canvas", e?.message); }
    return buildPreviewCanvas();
  };

  const buildPreviewCanvas = async () => {
    const pad = 0.5;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    items.forEach((it) => {
      const hIn = hOf(it);
      minX = Math.min(minX, it.xIn); minY = Math.min(minY, it.yIn);
      maxX = Math.max(maxX, it.xIn + it.wIn); maxY = Math.max(maxY, it.yIn + hIn);
    });
    minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
    maxX = Math.min(sheetW, maxX + pad); maxY = Math.min(sheetH, maxY + pad);
    const bboxW = Math.max(1, maxX - minX), bboxH = Math.max(1, maxY - minY);
    const W = 600, sc = W / bboxW, H = Math.max(80, Math.round(bboxH * sc));
    const canvas = document.createElement("canvas");
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d");
    await Promise.all(items.map((it) => new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      const timer = setTimeout(() => { console.warn("preview image timed out", it.src); resolve(); }, 8000);
      img.onload = () => {
        clearTimeout(timer);
        const hIn = hOf(it);
        const w = it.wIn * sc, h = hIn * sc;
        ctx.save();
        ctx.translate((it.xIn - minX) * sc + w / 2, (it.yIn - minY) * sc + h / 2);
        ctx.rotate(((it.rot || 0) * Math.PI) / 180);
        try { ctx.drawImage(img, -w / 2, -h / 2, w, h); } catch (e) { console.error("drawImage failed", e); }
        ctx.restore();
        resolve();
      };
      img.onerror = () => { clearTimeout(timer); resolve(); };
      img.src = it.src;
    })));
    return new Promise((resolve) => {
      const guard = setTimeout(() => resolve(null), 20000);
      let blobCb;
      try {
        blobCb = canvas.toBlob.bind(canvas);
      } catch { clearTimeout(guard); return resolve(null); }
      try {
        blobCb(async (blob) => {
          if (!blob) { clearTimeout(guard); return resolve(null); }
          const fd = new FormData();
          fd.append("file", new File([blob], "gang-sheet-preview.png", { type: "image/png" }));
          try { const { data } = await api.post("/upload", fd, { timeout: 15000 }); clearTimeout(guard); resolve(fileUrl(data.url)); }
          catch { clearTimeout(guard); resolve(null); }
        }, "image/png");
      } catch (e) { console.error("toBlob failed (tainted canvas?)", e); clearTimeout(guard); resolve(null); }
    });
  };

  const forOrder = searchParams.get("forOrder") === "1";
  const presetParam = searchParams.get("preset");
  const editLine = searchParams.get("editLine");
  const readDraft = () => { try { return JSON.parse(localStorage.getItem("awg_admin_order_draft") || "null") || {}; } catch { return {}; } };
  useEffect(() => {
    if (!presetParam || presets.length === 0) return;
    const pi = presets.findIndex((p) => p.name === presetParam);
    if (pi >= 0) setPresetIdx(pi);
    const h = Number(searchParams.get("height"));
    if (h > 0) setCustomHeight(h);
  }, [presets, presetParam]); // eslint-disable-line react-hooks/exhaustive-deps
  const [editLineLoaded, setEditLineLoaded] = useState(false);
  useEffect(() => {
    if (!forOrder || editLine == null || presets.length === 0 || editLineLoaded) return;
    const ln = (readDraft().lines || [])[+editLine];
    if (!ln || ln.kind !== "gangsheet") { toast.error("That order line is not a gang sheet"); setEditLineLoaded(true); return; }
    const pi = presets.findIndex((p) => p.name === ln.sheet_name);
    if (pi >= 0) setPresetIdx(pi);
    if (ln.sheet_height) setCustomHeight(ln.sheet_height);
    const loaded = (ln.layout || []).filter((l) => l.xIn != null).map((l) => ({ id: crypto.randomUUID(), src: l.src, natW: 300, natH: 300, xIn: l.xIn, yIn: l.yIn, wIn: l.wIn, rot: l.rot || 0, hIn: l.hIn ?? null, pending: true }));
    setItems(loaded); setNotes(ln.notes || ""); setQty(Number(ln.quantity) || 1);
    if (ln.service) setService(ln.service);
    if (ln.service === "press_own" && ln.press_qty != null) setPressQty(Number(ln.press_qty));
    loaded.forEach(loadDims);
    setEditLineLoaded(true);
    toast.success("Editing gang sheet from the customer's order");
  }, [forOrder, editLine, presets, editLineLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const addSheetToCart = async (force = false, confirmedPreview) => {
    if (items.length === 0) { toast.error("Add at least one design to the sheet"); return; }
    if (guardDims()) return;
    if (!force && lowDpiItems.length > 0) { setConfirmOpen(true); return; }
    if (confirmedPreview === undefined) { openPrintPreview((url) => addSheetToCart(true, url)); return; }
    const preview = confirmedPreview;
    if (forOrder) {
      const line = {
        kind: "gangsheet", name: `Gang Sheet — ${preset.name}${preset.height_in == null ? ` (${sheetH}")` : ""}${garmentLabel ? ` for ${garmentLabel}` : ""}`, quantity: qty, unit_price: linePrice, ...garmentFields, ...serviceFields,
        sheet_name: preset.name, sheet_width: sheetW, sheet_height: sheetH, image_url: preview || items[0].src, notes: notes.trim() || null,
        low_dpi: lowDpiItems.length > 0, low_dpi_count: lowDpiItems.length,
        layout: serializeLayout(),
      };
      const draft = readDraft();
      const lines = [...(draft.lines || [])];
      if (editLine != null && lines[+editLine]) lines[+editLine] = line; else lines.push(line);
      localStorage.setItem("awg_admin_order_draft", JSON.stringify({ ...draft, lines, saved_at: Date.now() }));
      toast.success(editLine != null ? "Gang sheet updated on the customer's order" : "Gang sheet added to the customer's order");
      navigate("/admin?tab=customers&openOrder=1");
      return;
    }
    addItem({
      kind: "gangsheet", name: `Gang Sheet — ${preset.name}${garmentLabel ? ` for ${garmentLabel}` : ""}`, quantity: qty, unit_price: linePrice, ...serviceFields,
      sheet_name: preset.name, sheet_width: sheetW, sheet_height: sheetH, ...garmentFields,
      image_url: preview || items[0].src, preview_url: preview || items[0].src,
      low_dpi: lowDpiItems.length > 0, low_dpi_count: lowDpiItems.length,
      notes: notes.trim() || null, sheet_saving: trimSaved > 0 ? trimSaved : null,
      layout: serializeLayout(),
    });
    setTrimSaved(0);
    // Reset the canvas so another gang sheet can be built & added to the same order
    setItems([]);
    setSelectedId(null);
    setDelSel(new Set());
    setNotes("");
    setDrawerOpen(true);
    toast.success("Gang sheet added — build another or head to checkout");
  };

  const editOrderId = searchParams.get("orderId");
  const editItemIdx = searchParams.get("item");
  useEffect(() => {
    if (!editOrderId || editItemIdx == null || presets.length === 0 || editCtx) return;
    (async () => {
      try {
        const { data: order } = await api.get(`/admin/orders/${editOrderId}`);
        const it = (order.items || [])[+editItemIdx];
        if (!it || it.kind !== "gangsheet") { toast.error("That order item is not a gang sheet"); return; }
        const pi = presets.findIndex((p) => p.name === it.sheet_name);
        if (pi >= 0) setPresetIdx(pi);
        if (it.sheet_height) setCustomHeight(it.sheet_height);
        const layout = (it.layout || []).filter((l) => l.xIn != null);
        const loaded = layout.map((l) => ({ id: crypto.randomUUID(), src: l.src, natW: 300, natH: 300, xIn: l.xIn, yIn: l.yIn, wIn: l.wIn, rot: l.rot || 0, hIn: l.hIn ?? null, pending: true }));
        setItems(loaded);
        setNotes(it.notes || "");
        setQty(Number(it.quantity) || 1);
        if (it.service) setService(it.service);
        if (it.service === "press_own" && it.press_qty != null) setPressQty(Number(it.press_qty));
        setEditCtx({ orderId: editOrderId, idx: +editItemIdx, orderNumber: order.order_number });
        loaded.forEach(loadDims);
        toast.success(`Editing gang sheet for order ${order.order_number}`);
      } catch { toast.error("Could not load the order's gang sheet"); }
    })();
  }, [presets, editOrderId, editItemIdx, editCtx]);

  const addSheetMode = searchParams.get("addSheet");
  useEffect(() => {
    if (!editOrderId || addSheetMode !== "1" || editItemIdx != null || presets.length === 0 || addCtx) return;
    (async () => {
      try {
        const { data: order } = await api.get(`/admin/orders/${editOrderId}`);
        setAddCtx({ orderId: editOrderId, orderNumber: order.order_number });
        toast.success(`Adding a gang sheet to order ${order.order_number}`);
      } catch { toast.error("Could not load that order"); }
    })();
  }, [presets, editOrderId, addSheetMode, editItemIdx, addCtx]);

  const addSheetToOrder = async () => {
    if (items.length === 0) { toast.error("Add at least one design to the sheet"); return; }
    if (guardDims()) return;
    setBuilding(true);
    let preview = null;
    try { preview = await buildPreview(); } catch (e) { console.error(e); }
    setBuilding(false);
    try {
      await api.post(`/admin/orders/${addCtx.orderId}/add-sheet`, {
        name: `Gang Sheet — ${preset.name}`, sheet_name: preset.name,
        sheet_width: sheetW, sheet_height: sheetH, unit_price: linePrice, quantity: qty, ...serviceFields,
        preview_url: preview || items[0]?.src, notes: notes.trim() || null,
        low_dpi: lowDpiItems.length > 0, low_dpi_count: lowDpiItems.length,
        layout: serializeLayout(),
        override: approveNow,
      });
      toast.success(approveNow ? `Added & approved on order ${addCtx.orderNumber}` : `Added to order ${addCtx.orderNumber} — approval email sent`);
      navigate("/admin");
    } catch (err) { toast.error("Could not add sheet to order"); }
  };

  const saveSheetEdit = async () => {
    if (items.length === 0) { toast.error("Sheet is empty"); return; }
    setBuilding(true);
    let preview = null;
    try { preview = await buildPreview(); } catch (e) { console.error(e); }
    setBuilding(false);
    try {
      await api.put(`/admin/orders/${editCtx.orderId}/sheet/${editCtx.idx}`, {
        layout: serializeLayout(),
        sheet_name: preset.name, sheet_width: sheetW, sheet_height: sheetH,
        preview_url: preview || items[0]?.src, notes: notes.trim() || null,
        unit_price: linePrice, ...serviceFields, low_dpi: lowDpiItems.length > 0, low_dpi_count: lowDpiItems.length,
        notify_customer: notifyCustomer,
      });
      toast.success(`Saved to order ${editCtx.orderNumber}${notifyCustomer ? " · proof emailed to customer" : " · customer not emailed"}`);
      navigate("/admin");
    } catch (e) { toast.error("Could not save changes to the order"); }
  };

  useEffect(() => {
    if (!librarySheetId || presets.length === 0 || libCtx) return;
    (async () => {
      try {
        const { data: s } = await api.get(`/admin/saved-sheets/${librarySheetId}`);
        const pi = presets.findIndex((p) => p.name === s.preset_name);
        if (pi >= 0) setPresetIdx(pi);
        if (s.sheet_height) setCustomHeight(s.sheet_height);
        const layout = (s.layout || []).filter((l) => l.xIn != null);
        const loaded = layout.map((l) => ({ id: crypto.randomUUID(), src: l.src, natW: 300, natH: 300, xIn: l.xIn, yIn: l.yIn, wIn: l.wIn, rot: l.rot || 0, hIn: l.hIn ?? null, pending: true }));
        setItems(loaded);
        setNotes(s.notes || "");
        setLibCtx({ sheetId: s.sheet_id, name: s.name, folderId: s.folder_id || "" });
        loaded.forEach(loadDims);
        toast.success(`Editing library sheet "${s.name}"`);
      } catch { toast.error("Could not load that saved sheet"); }
    })();
  }, [presets, librarySheetId, libCtx]);

  const openLibSave = () => {
    if (items.length === 0) { toast.error("Add at least one design to the sheet"); return; }
    if (guardDims()) return;
    setLibName(libCtx?.name || `Sheet — ${preset.name}`);
    setLibFolder(libCtx?.folderId || libraryFolder || "");
    setLibOpen(true);
  };
  const saveToLibrary = async () => {
    if (!libName.trim()) { toast.error("Name the sheet"); return; }
    setBuilding(true);
    let preview = null;
    try { preview = await buildPreview(); } catch (e) { console.error(e); }
    setBuilding(false);
    const payload = {
      name: libName.trim(), folder_id: libFolder || "", preset_name: preset.name,
      sheet_width: sheetW, sheet_height: sheetH, unit_price: price,
      preview_url: preview || items[0]?.src, notes: notes.trim() || null,
      low_dpi: lowDpiItems.length > 0, low_dpi_count: lowDpiItems.length,
      layout: serializeLayout(),
    };
    try {
      if (libCtx) {
        await api.put(`/admin/saved-sheets/${libCtx.sheetId}`, payload);
        setLibCtx((c) => ({ ...c, name: payload.name, folderId: payload.folder_id }));
        toast.success("Library sheet updated");
      } else {
        const { data } = await api.post("/admin/saved-sheets", payload);
        setLibCtx({ sheetId: data.sheet_id, name: data.name, folderId: data.folder_id || "" });
        toast.success("Saved to your library");
      }
      setLibOpen(false);
      qc.invalidateQueries({ queryKey: ["saved-sheets"] });
      qc.invalidateQueries({ queryKey: ["sheet-folders"] });
    } catch (err) { toast.error("Could not save to library"); }
  };

  // Quick batch
  const [batch, setBatch] = useState([]);  const addBatch = (d) => setBatch((p) => { const ex = p.find((x) => x.design_id === d.design_id); return ex ? p.map((x) => x.design_id === d.design_id ? { ...x, qty: x.qty + 1 } : x) : [...p, { design_id: d.design_id, title: d.title, image_url: d.image_url, qty: 1 }]; });
  const batchCount = batch.reduce((s, b) => s + b.qty, 0);
  const addBatchToCart = () => {
    if (batch.length === 0) { toast.error("Pick designs first"); return; }
    addItem({
      kind: "gangsheet", name: `Quick Batch Sheet — ${preset.name} (${batchCount} prints)`, quantity: qty, unit_price: price,
      sheet_name: preset.name, sheet_width: sheetW, sheet_height: sheetH,
      image_url: batch[0].image_url, preview_url: batch[0].image_url,
      notes: notes.trim() || null,
      layout: batch.map((b) => ({ title: b.title, src: fileUrl(b.image_url), qty: b.qty })),
    });
    setBatch([]);
    setNotes("");
    setDrawerOpen(true);
    toast.success("Batch sheet added — build another or head to checkout");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white">Gang Sheet Builder</h1>
          <p className="text-zinc-400 mt-2">Arrange designs on a 12" or 17" roll, or quick-batch by quantity. Live pricing & DPI preflight included.</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button onClick={() => navigate("/request-design")} data-testid="builder-request-design" variant="outline" className="border-[#ff6a13]/50 bg-transparent text-[#ff6a13] hover:bg-[#ff6a13]/10 rounded-full h-10 px-4 font-semibold shrink-0"><Wand2 size={15} className="mr-1" />Request a Design</Button>
          <BuilderGuideButton open={guideOpen} setOpen={setGuideOpen} />
        </div>
      </div>
      <BuilderGuide open={guideOpen} setOpen={setGuideOpen} />

      <Tabs defaultValue="interactive" className="mt-6">
        <TabsList className="bg-[#1e222d] border border-zinc-800">
          <TabsTrigger value="interactive" data-testid="tab-interactive" className="data-[state=active]:bg-[#ff6a13] data-[state=active]:text-white">Interactive Canvas</TabsTrigger>
          <TabsTrigger value="batch" data-testid="tab-batch" className="data-[state=active]:bg-[#ff6a13] data-[state=active]:text-white">Quick Batch</TabsTrigger>
        </TabsList>

        {/* INTERACTIVE */}
        <TabsContent value="interactive">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-4">
            {/* Sidebar */}
            <div className="lg:col-span-4 flex flex-col gap-4">
              <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-5">
                <p className="text-xs font-mono-p uppercase tracking-wider text-zinc-500 mb-2">Sheet Size<FieldHint text={HINTS.sheetSize} testid="hint-sheet-size" /></p>
                <div className="space-y-1.5">
                  {presets.map((p, i) => (
                    <button key={p.name} onClick={() => setPresetIdx(i)} data-testid={`preset-${i}`}
                      className={`w-full flex justify-between items-center text-sm px-3 py-2 rounded-lg border transition-colors ${presetIdx === i ? "border-[#ff6a13] bg-[#ff6a13]/10 text-white" : "border-zinc-800 bg-[#1e222d] text-zinc-300 hover:border-zinc-700"}`}>
                      <span>{p.name}</span>
                      <span className="font-mono-p text-xs">{p.price != null ? `$${p.price.toFixed(2)}` : `$14.50/sq ft`}</span>
                    </button>
                  ))}
                </div>
                {preset && preset.height_in == null && (
                  <div className="mt-3">
                    <p className="text-xs text-zinc-400 mb-1">Custom length (inches)<FieldHint text={HINTS.customLength} testid="hint-custom-length" /></p>
                    <NumField data-testid="custom-height" min={2} step={1} value={customHeight} onCommit={(n) => setCustomHeight(n)} className="bg-[#1e222d] border-zinc-700 text-white font-mono-p" />
                  </div>
                )}
              </div>

              {user && (
                <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-5" data-testid="templates-card">
                  <p className="text-xs font-mono-p uppercase tracking-wider text-zinc-500 mb-2">My Sheet Templates<FieldHint text={HINTS.templates} testid="hint-templates" /></p>
                  <div className="flex gap-2">
                    <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Template name" className="h-9 bg-[#1e222d] border-zinc-700 text-white" data-testid="template-name-input" />
                    <Button size="sm" onClick={saveTemplate} className="h-9 bg-[#ff6a13] hover:bg-[#ff8a3d] rounded-full shrink-0" data-testid="save-template-btn"><Save size={14} /></Button>
                  </div>
                  <div className="space-y-1.5 mt-3 max-h-40 overflow-y-auto">
                    {templates.map((t) => (
                      <div key={t.template_id} className="flex items-center gap-2 text-sm bg-[#1e222d] border border-zinc-800 rounded-lg px-2 py-1.5" data-testid="template-row">
                        <span className="flex-1 truncate text-zinc-300">{t.name}</span>
                        <button onClick={() => reorderTemplate(t)} className="text-[#ff6a13] hover:underline text-xs font-semibold" data-testid="reorder-template-btn" title="Load & add to cart in one click">Reorder</button>
                        <button onClick={() => loadTemplate(t)} className="text-[#22c3d6] hover:underline text-xs font-semibold" data-testid="load-template-btn">Load</button>
                        <button onClick={() => deleteTemplate(t.template_id)} data-testid="delete-template-btn" className="text-zinc-600 hover:text-red-400"><Trash2 size={13} /></button>
                      </div>
                    ))}
                    {templates.length === 0 && <p className="text-xs text-zinc-400 font-mono-p">Save a layout to reorder it in one click.</p>}
                  </div>
                </div>
              )}

              <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-5">
                {garment && (
                  <div className="mb-4 flex items-center gap-3 rounded-xl border border-[#22c3d6]/50 bg-[#22c3d6]/10 p-2.5" data-testid="builder-garment-banner">
                    {garment.image_url && <img src={thumbUrl(garment.image_url, 120)} alt="" className="h-10 w-10 rounded-lg object-cover shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-mono-p uppercase tracking-wider text-[#22c3d6]">Printing for your garment</p>
                      <p className="text-sm text-white font-semibold truncate" data-testid="builder-garment-label">{garmentLabel}</p>
                    </div>
                    <button type="button" onClick={() => { clearGarmentPick(); setGarment(null); }} aria-label="Clear garment" data-testid="builder-garment-clear" className="p-1.5 rounded-full text-zinc-400 hover:text-white hover:bg-zinc-800"><X size={14} /></button>
                  </div>
                )}
                <label className={`flex items-center justify-center gap-2 w-full border border-dashed rounded-xl py-4 cursor-pointer text-zinc-300 text-sm relative overflow-hidden ${upProg ? "border-[#22c3d6]" : "border-zinc-700 hover:border-[#ff6a13]"}`} data-testid="upload-label" title={HINTS.upload}>
                  {upProg && <span className="absolute inset-y-0 left-0 bg-[#22c3d6]/15 transition-[width] duration-200" style={{ width: `${upProg.pct}%` }} />}
                  <span className="relative flex items-center gap-2">{upProg ? <><Upload size={16} className="animate-pulse" /><span data-testid="upload-progress">Uploading{upProg.n > 1 ? ` ${upProg.n} files` : ""}… {upProg.pct}%</span></> : <><Upload size={16} /> Upload PNG / JPEG / PDF</>}</span>
                  <input type="file" accept=".png,.jpg,.jpeg,.pdf" multiple className="hidden" onChange={onUpload} disabled={!!upProg} data-testid="file-upload-input" />
                </label>
                <div className="flex justify-end mt-1.5"><GuidelinesLink testid="builder-guidelines-link" /></div>
                <p className="text-xs font-mono-p uppercase tracking-wider text-zinc-500 mt-4 mb-2">Add from catalog</p>
                <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                  {designs.map((d) => (
                    <button key={d.design_id} onClick={() => addDesign(d)} data-testid={`add-design-${d.design_id}`} title={d.title} className="checker rounded-lg aspect-square flex items-center justify-center p-1 border border-zinc-800 hover:border-[#ff6a13]">
                      <img src={thumbUrl(d.image_url, 240)} loading="lazy" alt="" className="max-w-full max-h-full object-contain" />
                    </button>
                  ))}
                </div>
              </div>

              {selected && (
                <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-5" data-testid="item-controls">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-mono-p uppercase tracking-wider text-zinc-500">Selected Item</p>
                    <span className="inline-flex items-center"><span className={`text-xs font-mono-p px-2 py-0.5 rounded border ${dpiColor(selDpi)}`} data-testid="dpi-badge">{selDpi} DPI</span><FieldHint text={HINTS.dpi} testid="hint-dpi" /></span>
                  </div>
                  <div className="flex items-center justify-between mt-3 mb-2">
                    <p className="text-xs text-zinc-400">Resize · {selected.wIn.toFixed(2)}" × {hOf(selected).toFixed(2)}"<FieldHint text={HINTS.slider} testid="hint-slider" /></p>
                    <div className="flex items-center gap-1 bg-[#1e222d] border border-zinc-700 rounded-full p-0.5" title={HINTS.unit}>
                      <button onClick={() => setUnit("in")} data-testid="unit-in" className={`px-2 py-0.5 rounded-full text-[11px] font-mono-p transition-colors ${unit === "in" ? "bg-[#ff6a13] text-white" : "text-zinc-400 hover:text-white"}`}>in</button>
                      <button onClick={() => setUnit("px")} data-testid="unit-px" className={`px-2 py-0.5 rounded-full text-[11px] font-mono-p transition-colors ${unit === "px" ? "bg-[#ff6a13] text-white" : "text-zinc-400 hover:text-white"}`}>px</button>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-zinc-300 mb-2 cursor-pointer" data-testid="aspect-lock-label">
                    <input type="checkbox" checked={aspectLock} onChange={(e) => { setAspectLock(e.target.checked); if (e.target.checked) updateItem(selected.id, { hIn: null }); }} data-testid="aspect-lock" className="accent-[#ff6a13] w-4 h-4" />
                    Lock aspect ratio (resize together)<FieldHint text={HINTS.aspectLock} testid="hint-aspect-lock" />
                  </label>
                  <Slider value={[selected.wIn]} min={1} max={sheetW - 0.5} step={0.5} onValueChange={([v]) => updateItem(selected.id, aspectLock ? { wIn: v, hIn: null } : { wIn: v })} data-testid="size-slider" />
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <div>
                      <p className="text-[11px] text-zinc-500 mb-1">Width ({unit})<FieldHint text={`${HINTS.width} ${HINTS.unit}`} testid="hint-width" /></p>
                      <NumField min={unit === "in" ? 0.5 : 150} max={unit === "in" ? sheetW - 0.25 : Math.round((sheetW - 0.25) * DPI)} step={unit === "in" ? 0.1 : 10} decimals={unit === "in" ? 2 : 0} value={unit === "in" ? selected.wIn : Math.round(selected.wIn * DPI)} onCommit={(raw) => { const win = Math.max(0.5, Math.min(sheetW - 0.25, unit === "in" ? raw : raw / DPI)); updateItem(selected.id, aspectLock ? { wIn: win, hIn: null } : { wIn: win }); }} data-testid="item-width" className="h-8 bg-[#1e222d] border-zinc-700 text-white font-mono-p" />
                    </div>
                    <div>
                      <p className="text-[11px] text-zinc-500 mb-1">Height ({unit})<FieldHint text={HINTS.height} testid="hint-height" /></p>
                      <NumField min={unit === "in" ? 0.5 : 150} step={unit === "in" ? 0.1 : 10} decimals={unit === "in" ? 2 : 0} value={unit === "in" ? hOf(selected) : Math.round(hOf(selected) * DPI)} onCommit={(raw) => { const hIn = Math.max(0.5, unit === "in" ? raw : raw / DPI); if (aspectLock) { const ratio = (selected.natW / selected.natH) || 1; updateItem(selected.id, { wIn: Math.max(0.5, Math.min(sheetW - 0.25, hIn * ratio)), hIn: null }); } else { updateItem(selected.id, { hIn }); } }} data-testid="item-height" className="h-8 bg-[#1e222d] border-zinc-700 text-white font-mono-p" />
                    </div>
                  </div>
                  <p className="text-[10px] text-zinc-600 mt-1">{aspectLock ? "Aspect locked · drag the orange corner to scale." : "Independent W×H · drag the orange corner to stretch."}</p>
                  {selDpi < LOW_DPI && (
                    <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 p-2.5" data-testid="dpi-warning">
                      <div className="flex items-start gap-2">
                        <AlertTriangle size={15} className="text-red-400 shrink-0 mt-0.5" />
                        <p className="text-[11px] text-red-300 leading-snug">Low resolution ({selDpi} DPI) at this size — the print may look blurry. Resize it to a crisp 300 DPI, or upload a higher-resolution image.</p>
                      </div>
                      <Button size="sm" onClick={() => fitTo300(selected)} data-testid="fit-300-btn" className="w-full mt-2 h-8 bg-[#10b981] hover:bg-[#0e9e70] text-white rounded-full font-semibold text-xs">
                        <Wand2 size={13} className="mr-1" />Resize to 300 DPI ({(selected.natW / DPI).toFixed(2)}" × {(selected.natH / DPI).toFixed(2)}")
                      </Button>
                    </div>
                  )}
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" variant="outline" onClick={() => updateItem(selected.id, { rot: (selected.rot + 90) % 360 })} className="flex-1 border-zinc-700 bg-transparent text-white hover:bg-zinc-800" data-testid="rotate-btn" title={HINTS.rotate}><RotateCw size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => duplicate(selected.id)} className="flex-1 border-zinc-700 bg-transparent text-white hover:bg-zinc-800" data-testid="dup-btn" title={HINTS.duplicate}><Copy size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => removeItem(selected.id)} className="flex-1 border-zinc-700 bg-transparent text-red-400 hover:bg-zinc-800" data-testid="del-btn" title={HINTS.delete}><Trash2 size={14} /></Button>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" variant="outline" onClick={() => setSaveImgSrc(selected.src)} className="flex-1 border-[#10b981]/40 bg-[#10b981]/10 text-[#10b981] hover:bg-[#10b981]/20" data-testid="save-image-btn" title="Save this image to the design catalog"><Save size={14} className="mr-1.5" />Save to catalog</Button>
                      <a href={fileUrl(selected.src)} download target="_blank" rel="noreferrer" data-testid="download-image-link" title="Download original image" className="h-8 px-3 inline-flex items-center rounded-md border border-zinc-700 text-[#ffd600] hover:bg-zinc-800 text-xs font-semibold"><Upload size={14} className="rotate-180" /></a>
                    </div>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setCropTarget(selected)} className="w-full mt-2 border-zinc-700 bg-transparent text-white hover:bg-zinc-800" data-testid="crop-btn" title="Crop this image or trim its transparent edges"><Crop size={14} className="mr-1.5" />Crop image</Button>
                  <Button size="sm" variant="outline" onClick={() => removeBg(selected)} disabled={bgRemovingId === selected.id} className="w-full mt-2 border-[#22c3d6]/40 bg-[#22c3d6]/10 text-[#22c3d6] hover:bg-[#22c3d6]/20 disabled:opacity-60" data-testid="remove-bg-btn" title={HINTS.removeBg}><Eraser size={14} className="mr-1.5" />{bgRemovingId === selected.id ? "Removing background…" : "Remove background"}</Button>
                  <div className="flex items-center gap-2 mt-2">
                    <label className="text-[11px] text-zinc-400 font-mono-p shrink-0">Copies<FieldHint text={HINTS.copies} testid="hint-copies" /></label>
                    <NumField min={1} max={50} step={1} value={copies} onCommit={(n) => setCopies(n)} data-testid="copies-input" className="h-8 w-16 bg-[#1e222d] border-zinc-700 text-white font-mono-p" />
                    <Button size="sm" variant="outline" onClick={() => duplicateN(selected.id, copies)} data-testid="add-copies-btn" className="flex-1 h-8 border-zinc-700 bg-transparent text-white hover:bg-zinc-800"><Copy size={13} className="mr-1" />Add {copies} {copies === 1 ? "copy" : "copies"}</Button>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => fillSheet(selected)} data-testid="fill-sheet-btn" title={HINTS.fillSheet} className="w-full mt-2 border-[#ffd600]/40 bg-[#ffd600]/10 text-[#ffd600] hover:bg-[#ffd600]/20"><Layers size={14} className="mr-1.5" />Fill sheet with this image</Button>
                </div>
              )}
            </div>

            {/* Canvas */}
            <div className="lg:col-span-8 flex flex-col gap-4">
              <div className="bg-[#0c0d10] border border-zinc-800 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Button size="sm" onClick={autoArrange} className="bg-[#22c3d6]/20 text-[#22c3d6] border border-[#22c3d6]/40 hover:bg-[#22c3d6]/30 rounded-full" data-testid="auto-nest-btn" title={HINTS.autoNest}><Wand2 size={14} className="mr-1" />Auto-Nest</Button><FieldHint text={HINTS.autoNest} testid="hint-auto-nest" side="bottom" />
                    {efficiency?.suggest && <Button size="sm" onClick={efficiency.suggest.apply} data-testid="trim-to-fit-btn" title={`Switch to ${efficiency.suggest.label}`} className="bg-[#10b981]/20 text-[#10b981] border border-[#10b981]/40 hover:bg-[#10b981]/30 rounded-full">Trim to fit{efficiency.suggest.saving > 0 ? ` · −$${efficiency.suggest.saving.toFixed(2)}` : ""}</Button>}
                    <span className="text-xs text-zinc-500 font-mono-p">{items.length} item(s) · fill {fill.toFixed(0)}%</span>
                    <button onClick={() => setSnapGrid((v) => !v)} data-testid="snap-grid-toggle" title={snapGrid ? "Snap to 0.25\" grid: on" : "Snap to 0.25\" grid: off"} className={`text-xs font-mono-p inline-flex items-center gap-1 border rounded-full px-2 py-0.5 ${snapGrid ? "text-[#22c3d6] border-[#22c3d6]/40 bg-[#22c3d6]/10" : "text-zinc-400 border-zinc-700 hover:border-white"}`}><Grid3x3 size={12} />Snap {snapGrid ? "on" : "off"}</button>
                    <button onClick={() => openPrintPreview()} disabled={building || items.length === 0} data-testid="print-preview-btn" title="See the exact sheet that will be printed" className="text-xs font-mono-p inline-flex items-center gap-1 border border-zinc-700 rounded-full px-2 py-0.5 text-zinc-300 hover:border-white disabled:opacity-50"><Eye size={12} />Preview print</button>
                    {lowDpiItems.length > 0 && <span className="text-xs text-red-400 font-mono-p flex items-center gap-1" data-testid="sheet-lowdpi-warning"><AlertTriangle size={12} />{lowDpiItems.length} low-res</span>}
                    {lowDpiItems.length > 0 && (
                      <button onClick={fixAllTo300} data-testid="fix-all-300-btn" title="Resize all low-resolution designs to a print-ready 300 DPI" className="text-xs text-[#10b981] font-semibold font-mono-p inline-flex items-center gap-1 border border-[#10b981]/40 bg-[#10b981]/10 rounded-full px-2 py-0.5 hover:bg-[#10b981]/20"><Wand2 size={12} />Fix all to 300 DPI</button>
                    )}
                    {items.length > 0 && (
                      <button onClick={selectAll} data-testid="select-all-btn" className="text-xs text-zinc-300 hover:text-white font-mono-p inline-flex items-center gap-1 border border-zinc-700 rounded-full px-2 py-0.5 hover:border-white">
                        {delSel.size === items.length ? <CheckSquare size={13} /> : <Square size={13} />}{delSel.size === items.length ? "Deselect all" : "Select all"}
                      </button>
                    )}
                    {items.length > 0 && (() => { const n = toDelete().size; return (
                      <Button size="sm" onClick={deleteSelected} disabled={n === 0} data-testid="delete-selected-btn" title={n ? `Delete ${n} selected (Del key)` : "Click an image, tick several, or Select all — then delete"} className={`h-7 rounded-full font-semibold border ${n ? "bg-red-500/20 text-red-400 border-red-500/40 hover:bg-red-500/30" : "bg-transparent text-zinc-500 border-zinc-800"}`}><Trash2 size={13} className="mr-1" />{n ? `Delete ${n}` : "Delete"}</Button>
                    ); })()}
                  </div>
                  <span className="text-xs text-zinc-500 font-mono-p">{sheetW}" × {sheetH}"</span>
                </div>
                <div className="overflow-auto max-h-[560px] rounded-lg" style={{ display: "flex", justifyContent: "center" }}
                  onMouseMove={onPointerMove} onMouseUp={onPointerUp} onMouseLeave={onPointerUp}>
                  <div ref={canvasRef} onMouseDown={() => setSelectedId(null)} data-testid="gang-sheet-canvas"
                    className="relative checker border-2 border-zinc-700 shrink-0"
                    style={{ width: CANVAS_W, height: canvasH }}>
                    {items.map((it) => {
                      const hIn = hOf(it);
                      const sel = it.id === selectedId;
                      return (
                        <div key={it.id} onMouseDown={(e) => onPointerDown(e, it)} data-testid="sheet-item"
                          data-overlap={overlapIds.has(it.id) ? "1" : undefined} className={`absolute cursor-move ${overlapIds.has(it.id) ? "ring-[3px] ring-red-500 z-10" : sel ? "ring-2 ring-[#ff6a13] z-10" : delSel.has(it.id) ? "ring-2 ring-[#22c3d6] z-10" : "ring-1 ring-zinc-600/50"}`}
                          style={{ left: it.xIn * scale, top: it.yIn * scale, width: it.wIn * scale, height: hIn * scale, transform: `rotate(${it.rot}deg)` }}>
                          <img src={thumbUrl(it.src, 800)} onError={(e) => { if (e.currentTarget.src !== it.src) e.currentTarget.src = it.src; }} alt="" draggable={false} className="w-full h-full object-fill pointer-events-none" />
                          <button onMouseDown={(e) => { e.stopPropagation(); toggleDel(it.id); }} data-testid="item-select-checkbox" title="Select for deletion" className="absolute -top-2 -right-2 z-20 w-5 h-5 rounded bg-[#0c0d10] border border-zinc-500 flex items-center justify-center hover:border-white">
                            {delSel.has(it.id) ? <CheckSquare size={14} className="text-[#22c3d6]" /> : <Square size={12} className="text-zinc-500" />}
                          </button>
                          {effDpi(it) < LOW_DPI && <div title={`Low resolution: ${effDpi(it)} DPI`} data-testid="item-lowdpi-badge" className="absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full bg-red-500 border-2 border-white flex items-center justify-center"><AlertTriangle size={10} className="text-white" /></div>}
                          {sel && <div onMouseDown={(e) => onResizeDown(e, it)} data-testid="resize-handle" title="Drag to resize" className="absolute w-4 h-4 bg-[#ff6a13] border-2 border-white rounded-sm cursor-nwse-resize" style={{ right: -8, bottom: -8 }} />}
                          {sel && (
                            <div onMouseDown={(e) => onRotateDown(e, it)} data-testid="rotate-handle" title="Drag to rotate freely" className="absolute z-20" style={{ left: "50%", top: -30, transform: "translateX(-50%)", cursor: "grab" }}>
                              <div className="w-px h-5 bg-[#22c3d6] mx-auto" />
                              <div className="w-4 h-4 rounded-full bg-[#22c3d6] border-2 border-white flex items-center justify-center -mt-0.5"><RotateCw size={9} className="text-white" /></div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {items.length === 0 && <div className="absolute inset-0 flex items-center justify-center text-zinc-600 text-sm font-mono-p">Drop designs here</div>}
                    {overlapIds.size > 0 && <div data-testid="overlap-warning" className="absolute top-2 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-red-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg"><AlertTriangle size={13} />{overlapIds.size} designs overlap<button onClick={fixOverlaps} data-testid="fix-overlaps-btn" className="ml-1 bg-white text-red-600 rounded-full px-2 py-0.5 font-bold hover:bg-red-50">Fix overlaps</button></div>}
                  </div>
                </div>
              </div>

              {efficiency && (
                <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-4 flex flex-wrap items-center gap-4" data-testid="efficiency-card">
                  <div className="flex-1 min-w-[180px]">
                    <p className="text-xs font-mono-p uppercase tracking-wider text-zinc-500 mb-1.5">Sheet efficiency</p>
                    <div className="h-2 rounded-full bg-zinc-800 overflow-hidden"><div className={`h-full rounded-full ${fill >= 70 ? "bg-[#10b981]" : fill >= 40 ? "bg-[#ffd600]" : "bg-[#ff6a13]"}`} style={{ width: `${fill}%` }} /></div>
                    <p className="text-xs text-zinc-400 mt-1.5 font-mono-p" data-testid="efficiency-stats">{fill.toFixed(0)}% used · <span className={efficiency.wastePct > 50 ? "text-[#ff6a13]" : ""}>{efficiency.wastePct.toFixed(0)}% empty</span> · designs reach {contentBottom.toFixed(1)}" of {sheetH}"</p>
                  </div>
                  {efficiency.over && <span className="text-xs text-red-400 font-semibold flex items-center gap-1" data-testid="efficiency-over"><AlertTriangle size={13} />Designs run past the sheet — use at least {neededLen}"</span>}
                  {efficiency.suggest && (
                    <button onClick={efficiency.suggest.apply} data-testid="efficiency-suggest-btn" className="text-xs font-semibold inline-flex items-center gap-1.5 border border-[#10b981]/40 bg-[#10b981]/10 text-[#10b981] rounded-full px-3 py-1.5 hover:bg-[#10b981]/20">
                      <Wand2 size={13} />Switch to {efficiency.suggest.label}{efficiency.suggest.saving > 0 ? ` · save $${efficiency.suggest.saving.toFixed(2)}` : ""}
                    </button>
                  )}
                  {!efficiency.suggest && !efficiency.over && <span className="text-xs text-[#10b981] font-mono-p" data-testid="efficiency-ok">Best-fit length for this layout</span>}
                </div>
              )}
              <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-5">
                <label className="text-xs font-mono-p uppercase tracking-wider text-zinc-500 mb-2 block">Notes / special instructions <span className="text-zinc-600 normal-case tracking-normal">(optional)</span><FieldHint text={HINTS.notes} testid="hint-notes" /></label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="gangsheet-notes" placeholder="e.g. leave 1&quot; gap between images, matte finish, call me before printing…" className="bg-[#1e222d] border-zinc-700 text-white min-h-[70px] resize-y" />
              </div>

              <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-5" data-testid="service-panel">
                <p className="text-xs font-mono-p uppercase tracking-wider text-zinc-500 mb-2">Service</p>
                <div className="grid sm:grid-cols-3 gap-2">
                  {SERVICES.map((s) => (
                    <button key={s.id} type="button" onClick={() => setService(s.id)} data-testid={`service-${s.id}`} title={s.hint}
                      className={`text-left rounded-xl border px-3 py-2.5 transition-colors ${service === s.id ? "border-[#ff6a13] bg-[#ff6a13]/10 text-white" : "border-zinc-800 bg-[#1e222d] text-zinc-300 hover:border-zinc-600"}`}>
                      <p className="text-sm font-semibold leading-tight">{s.label}</p>
                      <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{s.id === "press_own" ? `$${pressFee.toFixed(2)} per garment` : s.id === "press_merch" ? "Included with merch" : "No pressing"}</p>
                    </button>
                  ))}
                </div>
                {service === "press_own" && (
                  <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="press-qty-row">
                    <label className="text-xs text-zinc-400">Garments you're supplying</label>
                    <NumField min={1} max={500} step={1} value={effPressQty} onCommit={(n) => setPressQty(n)} data-testid="press-qty" className="h-8 w-20 bg-[#1e222d] border-zinc-700 text-white font-mono-p" />
                    <span className="text-xs text-zinc-500 font-mono-p" data-testid="press-fee-total">Pressing: {effPressQty} × ${pressFee.toFixed(2)} = ${(effPressQty * pressFee).toFixed(2)} per sheet</span>
                    <button type="button" onClick={() => setPressQty(null)} className="text-[11px] text-[#22c3d6] hover:underline">match designs ({Math.max(1, items.length)})</button>
                  </div>
                )}
              </div>

              <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-mono-p uppercase tracking-wider text-zinc-500">{service === "press_own" ? "Sheet + pressing" : "Sheet Price"}<FieldHint text={HINTS.price} testid="hint-price" /></p>
                  <p className="font-display text-3xl font-black text-white font-mono-p" data-testid="sheet-price">${linePrice.toFixed(2)}</p>
                  <p className="text-[11px] text-zinc-500 font-mono-p mt-0.5" data-testid="sqft-rate">{service === "press_own" ? `$${price.toFixed(2)} sheet + $${(effPressQty * pressFee).toFixed(2)} pressing` : "$14.50 / sq ft"}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center"><Minus size={14} /></button>
                    <span className="w-8 text-center font-mono-p" data-testid="sheet-qty">{qty}</span>
                    <button onClick={() => setQty(qty + 1)} className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center"><Plus size={14} /></button>
                    <FieldHint text={HINTS.qty} testid="hint-qty" />
                  </div>
                  {editCtx ? (
                    <div className="flex items-center gap-3 flex-wrap">
                    <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer mr-2"><input type="checkbox" checked={notifyCustomer} onChange={(e) => setNotifyCustomer(e.target.checked)} className="accent-[#ff6a13] w-4 h-4" data-testid="edit-notify-customer" />Email customer the updated proof</label>
                    <Button onClick={saveSheetEdit} disabled={building} data-testid="save-sheet-edit" className="bg-[#10b981] hover:bg-[#0e9e70] disabled:opacity-60 text-white rounded-full h-11 px-6 font-semibold active:scale-95 transition-transform"><Save size={16} className="mr-1" />{building ? "Saving…" : `Save to Order ${editCtx.orderNumber}`}</Button>
                    </div>
                  ) : addCtx ? (
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer select-none" data-testid="approve-now-toggle">
                        <input type="checkbox" checked={approveNow} onChange={(e) => setApproveNow(e.target.checked)} className="accent-[#ff6a13] w-4 h-4" />
                        Approve now (skip email)
                      </label>
                      <Button onClick={addSheetToOrder} disabled={building} data-testid="add-sheet-to-order" className="bg-[#10b981] hover:bg-[#0e9e70] disabled:opacity-60 text-white rounded-full h-11 px-6 font-semibold active:scale-95 transition-transform"><Layers size={16} className="mr-1" />{building ? "Adding…" : `Add to Order ${addCtx.orderNumber}`}</Button>
                    </div>
                  ) : forOrder ? (
                    <div className="flex items-center gap-2">
                      <Button onClick={() => navigate("/admin?tab=customers&openOrder=1")} variant="outline" data-testid="back-to-order-btn" className="border-zinc-700 text-zinc-200 hover:bg-zinc-800 rounded-full h-11 px-4 font-semibold">Back to order</Button>
                      <Button onClick={() => addSheetToCart()} disabled={building} data-testid="add-sheet-to-customer-order" className="bg-[#10b981] hover:bg-[#0e9e70] disabled:opacity-60 text-white rounded-full h-11 px-6 font-semibold active:scale-95 transition-transform"><Layers size={16} className="mr-1" />{building ? "Saving preview…" : editLine != null ? "Save Changes to Customer's Order" : "Add Sheet to Customer's Order"}</Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      {cartItems.filter((i) => i.kind === "gangsheet").length > 0 && (
                        <Button onClick={() => navigate("/cart")} variant="outline" data-testid="view-cart-btn" className="border-zinc-700 text-zinc-200 hover:bg-zinc-800 rounded-full h-11 px-4 font-semibold">
                          Cart ({cartItems.filter((i) => i.kind === "gangsheet").length})
                        </Button>
                      )}
                      {isAdmin && (
                        <Button onClick={openLibSave} variant="outline" data-testid="save-to-library-btn" className="border-[#22c3d6]/50 bg-transparent text-[#22c3d6] hover:bg-[#22c3d6]/10 rounded-full h-11 px-4 font-semibold"><Save size={16} className="mr-1" />{libCtx ? "Update Library" : "Save to Library"}</Button>
                      )}
                      <Button onClick={() => addSheetToCart()} disabled={building} data-testid="add-sheet-to-cart" className="bg-[#ff6a13] hover:bg-[#ff8a3d] disabled:opacity-60 text-white rounded-full h-11 px-6 font-semibold active:scale-95 transition-transform"><Layers size={16} className="mr-1" />{building ? "Saving preview…" : "Add Sheet to Cart"}</Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* BATCH */}
        <TabsContent value="batch">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-4">
            <div className="lg:col-span-8 bg-[#12141a] border border-zinc-800 rounded-2xl p-5">
              <p className="text-xs font-mono-p uppercase tracking-wider text-zinc-500 mb-3">Pick designs & quantities</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {designs.map((d) => (
                  <button key={d.design_id} onClick={() => addBatch(d)} data-testid={`batch-add-${d.design_id}`} className="checker rounded-xl aspect-square flex items-center justify-center p-2 border border-zinc-800 hover:border-[#ff6a13] transition-colors">
                    <img src={thumbUrl(d.image_url, 240)} loading="lazy" alt={d.title} className="max-w-full max-h-full object-contain" />
                  </button>
                ))}
              </div>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-4">
              <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-5">
                <p className="text-xs font-mono-p uppercase tracking-wider text-zinc-500 mb-2">Sheet Size</p>
                <div className="space-y-1.5">
                  {presets.map((p, i) => (
                    <button key={p.name} onClick={() => setPresetIdx(i)} className={`w-full flex justify-between text-sm px-3 py-2 rounded-lg border ${presetIdx === i ? "border-[#ff6a13] bg-[#ff6a13]/10 text-white" : "border-zinc-800 bg-[#1e222d] text-zinc-300"}`}>
                      <span>{p.name}</span><span className="font-mono-p text-xs">{p.price != null ? `$${p.price.toFixed(2)}` : `$14.50/sq ft`}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-5">
                <p className="text-xs font-mono-p uppercase tracking-wider text-zinc-500 mb-2">Selected ({batchCount})</p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {batch.map((b) => (
                    <div key={b.design_id} className="flex items-center gap-2 text-sm">
                      <img src={thumbUrl(b.image_url, 240)} loading="lazy" alt="" className="w-8 h-8 object-contain checker rounded" />
                      <span className="flex-1 truncate text-zinc-300">{b.title}</span>
                      <span className="font-mono-p text-zinc-400">×{b.qty}</span>
                    </div>
                  ))}
                  {batch.length === 0 && <p className="text-xs text-zinc-600 font-mono-p">None selected</p>}
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-800">
                  <span className="font-display text-2xl font-black text-white font-mono-p">${price.toFixed(2)}</span>
                  <Button onClick={addBatchToCart} data-testid="add-batch-to-cart" className="bg-[#ff6a13] hover:bg-[#ff8a3d] text-white rounded-full font-semibold">Add to Cart</Button>
                </div>
              </div>
              <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-5">
                <label className="text-xs font-mono-p uppercase tracking-wider text-zinc-500 mb-2 block">Notes / special instructions</label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="gangsheet-notes-batch" placeholder="Any special instructions for this sheet…" className="bg-[#1e222d] border-zinc-700 text-white min-h-[60px] resize-y" />
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={libOpen} onOpenChange={setLibOpen}>
        <DialogContent className="bg-[#12141a] border-zinc-800 text-zinc-100 max-w-md" data-testid="save-library-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Save size={18} className="text-[#22c3d6]" />{libCtx ? "Update library sheet" : "Save to library"}</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400 pt-1">Save this layout to your admin library — no order needed. Re-open it, download a print-ready 300 DPI PNG, or attach it to an order later.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            <div>
              <p className="text-[11px] text-zinc-500 mb-1 font-mono-p uppercase tracking-wider">Sheet name</p>
              <Input value={libName} onChange={(e) => setLibName(e.target.value)} data-testid="lib-name-input" placeholder="e.g. Summer batch — 12x24" className="bg-[#1e222d] border-zinc-700 text-white" />
            </div>
            <div>
              <p className="text-[11px] text-zinc-500 mb-1 font-mono-p uppercase tracking-wider">Folder</p>
              <select value={libFolder} onChange={(e) => setLibFolder(e.target.value)} data-testid="lib-folder-select" className="w-full h-10 bg-[#1e222d] border border-zinc-700 rounded-lg text-white text-sm px-3">
                <option value="">Unfiled</option>
                {sheetFolders.map((f) => <option key={f.folder_id} value={f.folder_id}>{f.name}</option>)}
              </select>
            </div>
            <Button onClick={saveToLibrary} disabled={building} data-testid="lib-save-confirm" className="w-full bg-[#ff6a13] hover:bg-[#ff8a3d] disabled:opacity-60 rounded-full font-semibold">{building ? "Saving…" : libCtx ? "Update sheet" : "Save sheet"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <CropDialog src={cropTarget?.src} open={!!cropTarget} onClose={() => setCropTarget(null)} onCropped={(url, nw, nh) => {
        const it = items.find((i) => i.id === cropTarget?.id);
        if (!it) return;
        const upd = { src: url, hIn: null };
        if (nw && nh) { upd.natW = nw; upd.natH = nh; if (it.natW) upd.wIn = Math.max(0.5, Math.min(sheetW - 0.25, +(it.wIn * nw / it.natW).toFixed(2))); }
        updateItem(it.id, upd);
      }} />
      <SaveImageToCatalog src={saveImgSrc} open={!!saveImgSrc} onClose={() => setSaveImgSrc(null)} />
      <Dialog open={!!printPreview} onOpenChange={(o) => { if (!o) setPrintPreview(null); }}>
        <DialogContent className="bg-[#12141a] border-zinc-800 text-zinc-100 max-w-2xl" data-testid="print-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Eye size={18} className="text-[#22c3d6]" />Print preview — {sheetW}" × {sheetH}"</DialogTitle>
            <DialogDescription className="text-sm text-zinc-300 pt-1">This is the exact sheet our printer receives, rendered by the same engine as the print file.{overlapIds.size > 0 && <span className="text-red-400 font-semibold"> {overlapIds.size} designs overlap.</span>}</DialogDescription>
          </DialogHeader>
          {printPreview?.url && <div className="max-h-[60vh] overflow-auto rounded-lg bg-[#0c0d10] border border-zinc-800 p-2 flex justify-center"><img src={printPreview.url} alt="Print preview" data-testid="print-preview-img" className="max-w-full h-auto" style={{ backgroundImage: "linear-gradient(45deg,#1e222d 25%,transparent 25%,transparent 75%,#1e222d 75%),linear-gradient(45deg,#1e222d 25%,transparent 25%,transparent 75%,#1e222d 75%)", backgroundSize: "16px 16px", backgroundPosition: "0 0,8px 8px" }} /></div>}
          <div className="flex gap-2 justify-end mt-2">
            <Button variant="outline" onClick={() => setPrintPreview(null)} data-testid="print-preview-close" className="border-zinc-700 bg-transparent text-white hover:bg-zinc-800 rounded-full">{printPreview?.onConfirm ? "Go back & edit" : "Close"}</Button>
            {printPreview?.onConfirm && <Button onClick={() => { const { url, onConfirm } = printPreview; setPrintPreview(null); onConfirm(url); }} data-testid="print-preview-confirm" className="bg-[#ff6a13] hover:bg-[#ff8a3d] text-white rounded-full">Looks good — add sheet</Button>}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-[#12141a] border-zinc-800 text-zinc-100 max-w-md" data-testid="lowdpi-confirm">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><AlertTriangle size={18} className="text-red-400" />Low-resolution designs</DialogTitle>
            <DialogDescription className="text-sm text-zinc-300 pt-1">
              {lowDpiItems.length} design{lowDpiItems.length === 1 ? "" : "s"} on this sheet {lowDpiItems.length === 1 ? "is" : "are"} below {LOW_DPI} DPI at the current size and may print blurry. You can make them smaller for a sharper print, or add the sheet anyway.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end mt-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="lowdpi-cancel" className="border-zinc-700 bg-transparent text-white hover:bg-zinc-800 rounded-full">Go back &amp; fix</Button>
            <Button onClick={() => { setConfirmOpen(false); addSheetToCart(true); }} data-testid="lowdpi-proceed" className="bg-[#ff6a13] hover:bg-[#ff8a3d] text-white rounded-full">Add anyway</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
